import { observer } from 'mobx-react-lite';

import { Button } from '@components/button';
import { FormError } from '@components/form-error';
import { InfoTooltip } from '@components/info-tooltip';
import { Select } from '@components/select';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { TextField } from '@components/text-field';

import { useAuthenticatedUser } from 'features/authentication';
import { useStore } from 'models/Provider';
import { IJob, IUser } from 'models/types';

import { CreateUserJobAccessForm } from './create-user-job-access-form';

export const CreateUserForm = observer(
  ({
    allowSend = true,
    close,
    disabled,
    editing = false,
    errors,
    jobs,
    save,
    user,
  }: {
    allowSend?: boolean;
    close: () => void;
    disabled: boolean;
    editing?: boolean;
    errors: string[][];
    jobs: IJob[];
    save: () => void;
    user: IUser;
  }) => {
    const { t } = useStore();
    const me = useAuthenticatedUser();
    const { name, email, role, shouldSendInvite } = user;
    const userTypes = [
      {
        value: 'user',
        label: t('user.role.user'),
        helpText: t('user.role.user.help'),
      },
      {
        value: 'limited',
        label: t('user.role.limited'),
        helpText: t('user.role.limited.help'),
      },
      {
        value: 'recruiter',
        label: t('user.role.recruiter'),
        helpText: t('user.role.recruiter.help'),
      },
      {
        value: 'admin',
        label: t('user.role.admin'),
        helpText: t('user.role.admin.help'),
      },
      {
        value: 'superadmin',
        label: t('user.role.superadmin'),
        helpText: t('user.role.superadmin.help'),
        isDisabled: me.role !== 'superadmin',
      },
    ];

    let errorObj: Record<string, string> = {};
    for (let [key, val] of errors.entries()) {
      errorObj[key] = val.slice().join(', ');
    }

    // We'll show errors for name/email below their own inputs.
    // Only do this global error container for other errors.
    const errorContainer = Object.keys(errorObj)
      .filter((key) => key !== 'email' && key !== 'name')
      .map((errorKey, i) => {
        return <FormError key={i} text={errorObj[errorKey]} />;
      });

    return (
      <Stack space={6}>
        {errorContainer}
        <Stack space={4}>
          <TextField
            label={t('user.create.label.name')}
            defaultValue={name}
            onChange={user.setName}
            error={errorObj.name}
            data-cy="user-name"
            isDisabled={editing}
          />
          <TextField
            label={t('user.create.label.email')}
            defaultValue={email}
            onChange={user.setEmail}
            error={errorObj.email}
            data-cy="user-email"
            isDisabled={editing}
          />
          <Select
            label={t('user.create.label.role')}
            defaultValue={role}
            isClearable={false}
            isSearchable={false}
            isDisabled={user.role === 'superadmin' && me.role !== 'superadmin'}
            options={userTypes}
            onChange={user.setRole}
          />
          {role === 'admin' && (
            <Stack space={0} orientation="horizontal" justify="space-between">
              <Switch
                defaultValue={user.addToNewJobs}
                onChange={user.toggleAddToNewJobs}
                label={t('user.create.addtonewjobs.toggle')}
              />
              <InfoTooltip tooltip={t('user.create.addtonewjobs.tooltip')} />
            </Stack>
          )}
          {(role === 'admin' || role === 'recruiter') && (
            <>
              <Stack space={0} orientation="horizontal" justify="space-between">
                <Switch
                  defaultValue={user.addToHiredApplications}
                  onChange={user.toggleAddToHiredApplications}
                  label={t('user.create.addtohiredapplications.toggle')}
                />
                <InfoTooltip tooltip={t('user.create.addtohiredapplications.tooltip')} />
              </Stack>
              <CreateUserJobAccessForm user={user} jobs={jobs} />
            </>
          )}

          {allowSend && (
            <Switch
              defaultValue={shouldSendInvite}
              data-cy="user-send-invite"
              onChange={user.toggleShouldSendInvite}
              isDisabled={disabled}
              label={t('user.create.sendinvite.toggle')}
            />
          )}
        </Stack>

        <Stack orientation="horizontal" space={4} justify="flex-end">
          <Button variant="secondary" onClick={close} text={t('user.create.button.cancel')} />
          <Button
            variant="primary"
            onClick={save}
            disabled={disabled}
            data-cy="user-save"
            text={t('user.create.button.submit')}
          />
        </Stack>
      </Stack>
    );
  },
);
